import { probeGetSample } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { isEmpty } from 'lodash';
import isBoolean from 'lodash/isBoolean';
import { ModalDestinationSchema } from 'new/elements/flows/ModalDestinationSchema';
import { ModalPreviewSamples } from 'new/elements/flows/ModalPreviewSamples';
import { ModalDefault } from 'new/routes/ModalDefault';
import React, {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useImmer } from 'use-immer';

export type DatabaseSelection = {
  checked?: boolean;
  expanded?: boolean;
  selectionCount?: number;
  tables?: {
    [tableName: string]: {
      checked?: boolean;
      expanded?: boolean;
    };
  };
};

export type DatabasesSelection = Record<string, DatabaseSelection> & {
  syncAll?: boolean;
};

type ContextType = {
  selection: { [tableName: string]: boolean | number } & {
    __selectionCount__?: number;
  };
  resyncSelection: { [tableName: string]: boolean };
  tableFilters: { [tableName: string]: string };
  tableMap: { [key: string]: any };
  columnSelections: {
    [tableName: string]: {
      timestampColumn?: string;
      autoIncrementColumn?: string;
    };
  };
  syncModes: { [tableName: string]: string };
  syncAll: boolean;
  databaseSelected: boolean;
  showPreview: boolean;
  expandedTables: { [tableName: string]: boolean };
  table: any;
  isDestination?: boolean;
  credentialId: number;
  selectDatabase: (databaseName: string, checked: boolean) => void;
  onChangeSyncAll?: (node: any) => void;
  onShowSamples?: (database: any, table: any) => void;
  onShowSchema?: (table: any) => void;
  onRequestProbeTree?: (node: any) => void;
  parentScrollRef?: RefObject<HTMLDivElement>;
  expandTable: (tableName: string, databaseName: string, expanded: boolean) => void;
  selectTables: (tableNames: string[], databaseName: string, checked: boolean) => void;
  selectResyncTables: (tableNames: string[], databaseName: string, checked: boolean) => void;
  addTableFilters: (tableName: string, filter: string) => void;
  updateTableFilter: (tableName: string, filter: string) => void;
  updateColumnSelection: (
    tableName: string,
    columnName: string,
    columnType: 'timestamp' | 'autoIncrement',
    isSelected: boolean,
  ) => void;
  updateSyncMode: (tableName: string, syncMode: string) => void;
};

const DatabasesContext = createContext<ContextType>({
  selection: {
    __selectionCount__: 0,
  },
  resyncSelection: {},
  tableFilters: {},
  columnSelections: {},
  syncModes: {},
  syncAll: true,
  databaseSelected: false,
  showPreview: false,
  expandedTables: {},
  table: null,
  isDestination: false,
  credentialId: 0,
  tableMap: {},
  selectDatabase: (databaseName: string, checked: boolean) => {},
  onShowSamples: (node) => {},
  expandTable: (tableName, databaseName, expanded) => {},
  selectTables: (tableNames: string[], databaseName: string, checked: boolean) => {},
  selectResyncTables: (tableNames: string[], databaseName: string, checked: boolean) => {},
  addTableFilters: (tableName: string, filter: string) => {},
  updateTableFilter: (tableName: string, filter: string) => {},
  updateColumnSelection: (
    tableName: string,
    columnName: string,
    columnType: 'timestamp' | 'autoIncrement',
    isSelected: boolean,
  ) => {},
  updateSyncMode: (tableName: string, syncMode: string) => {},
});

export default function useDatabases() {
  return useContext(DatabasesContext);
}

export type MappingType = {
  [table: string]: {
    [sourceColumn: string]: {
      checked?: boolean;
      primary?: boolean;
      name?: string;
    };
  };
};

export interface DatabasesProps {
  credentialId: number;
  isDestination?: boolean;
  initialSelection?: ContextType['selection'];
  initialMapping?: MappingType;
  initialResyncSelection?: ContextType['resyncSelection'];
  initialTableFilters?: ContextType['tableFilters'];
  initialColumnSelections?: ContextType['columnSelections'];
  initialSyncModes?: ContextType['syncModes'];
  databases: { id: string; name: string; children: any[] }[];
  needsSelection?: boolean;
  sourceConnector?: any;
  sinkConnector?: any;
  tableMap?: any;
  isCDCMode?: boolean;
  onSelectedTablesChange?: (selection: ContextType['selection']) => void;
  onMappingChange?: (mapping: MappingType) => void;
  onResyncSelectionChange?: (resyncSelection: ContextType['resyncSelection']) => void;
  onTableFiltersChange?: (tableFilters: ContextType['tableFilters']) => void;
  onColumnSelectionsChange?: (columnSelections: ContextType['columnSelections']) => void;
  onSyncModesChange?: (syncModes: ContextType['syncModes']) => void;
  parentScrollRef?: RefObject<HTMLDivElement>;
}

export const DatabasesProvider = (props: PropsWithChildren<DatabasesProps>) => {
  const { children, needsSelection, credentialId, databases, parentScrollRef } = props;
  const [selection, setSelection] = useState<ContextType['selection']>(
    props.initialSelection || {},
  );
  const [resyncSelection, setResyncSelection] = useState<ContextType['resyncSelection']>(
    props.initialResyncSelection || {},
  );
  const [tableFilters, setTableFilters] = useState<ContextType['tableFilters']>(
    props.initialTableFilters || {},
  );
  const [columnSelections, setColumnSelections] = useState<ContextType['columnSelections']>(
    props.initialColumnSelections || {},
  );
  const [syncModes, setSyncModes] = useState<ContextType['syncModes']>(
    props.initialSyncModes || {},
  );
  const [state, setState] = useImmer({
    syncAll: true,
    databaseSelected: false,
    showPreview: false,
    expandedTables: {},
    table: null,
    previewDatabase: undefined as undefined | string,
    previewTable: undefined as undefined | string,
  });
  const [tableMap, setTableMap] = useState(props.initialMapping || {});
  const mapRef = useRef({});
  const dispatch = useAppDispatch();

  const updateSelection = useCallback(
    (updater: (selection: ContextType['selection']) => void) => {
      if (needsSelection) {
        setSelection((selection) => {
          const newSelection = { ...selection };
          updater(newSelection);
          if (typeof props.onSelectedTablesChange === 'function') {
            props.onSelectedTablesChange(newSelection);
          }
          return newSelection;
        });
      }
    },
    [needsSelection],
  );

  const updateResyncSelection = useCallback(
    (updater: (resyncSelection: ContextType['resyncSelection']) => void) => {
      setResyncSelection((resyncSelection) => {
        const newResyncSelection = { ...resyncSelection };
        updater(newResyncSelection);
        if (typeof props.onResyncSelectionChange === 'function') {
          props.onResyncSelectionChange(newResyncSelection);
        }
        return newResyncSelection;
      });
    },
    [props.onResyncSelectionChange],
  );

  const updateTableFilters = useCallback(
    (updater: (tableFilters: ContextType['tableFilters']) => void) => {
      setTableFilters((tableFilters) => {
        const newTableFilters = { ...tableFilters };
        updater(newTableFilters);
        if (typeof props.onTableFiltersChange === 'function') {
          props.onTableFiltersChange(newTableFilters);
        }
        return newTableFilters;
      });
    },
    [props.onTableFiltersChange],
  );

  const updateColumnSelection = useCallback(
    (
      tableName: string,
      columnName: string,
      columnType: 'timestamp' | 'autoIncrement',
      isSelected: boolean,
    ) => {
      setColumnSelections((columnSelections) => {
        const newColumnSelections = { ...columnSelections };

        if (isSelected) {
          if (columnType === 'timestamp') {
            newColumnSelections[tableName] = {
              timestampColumn: columnName,
              autoIncrementColumn: null,
            };
          } else {
            newColumnSelections[tableName] = {
              timestampColumn: null,
              autoIncrementColumn: columnName,
            };
          }
        } else {
          // this is for the case when the user deselects the column
          newColumnSelections[tableName] = {
            timestampColumn: null,
            autoIncrementColumn: null,
          };
        }

        if (typeof props.onColumnSelectionsChange === 'function') {
          props.onColumnSelectionsChange(newColumnSelections);
        }
        return newColumnSelections;
      });
    },
    [props.onColumnSelectionsChange],
  );

  const updateSyncMode = useCallback(
    (tableName: string, syncMode: string) => {
      setSyncModes((syncModes) => {
        const newSyncModes = { ...syncModes };
        newSyncModes[tableName] = syncMode;

        if (typeof props.onSyncModesChange === 'function') {
          props.onSyncModesChange(newSyncModes);
        }
        return newSyncModes;
      });
    },
    [props.onSyncModesChange],
  );

  const onChangeSyncAll = (e) => {
    const value = e.target.checked;
    setState((state) => {
      state.syncAll = value;
    });
    selectTables(
      databases[0]?.children.map((t) => t.name),
      databases[0].name,
      !value,
    );
  };

  const onShowSamples = useCallback(
    (database, table) => {
      setState((state) => {
        state.showPreview = true;
        state.previewDatabase = database?.name;
        state.previewTable = table?.name;
      });
      dispatch(
        probeGetSample(credentialId, 0, {
          db_query_mode: 'Default',
          database: database.name,
          table: table.name,
        }),
      );
    },
    [credentialId],
  );

  const onShowSchema = useCallback((table) => {
    setState((state) => {
      state.table = table;
    });
  }, []);

  const selectDatabase = (databaseName: string, checked: boolean) => {
    setState((state) => {
      state.databaseSelected = checked;
    });
    updateSelection((selection) => {
      if (!checked) {
        Object.keys(selection).forEach((tableName) => {
          selection[tableName] = false;
        });
        selection.__selectionCount__ = 0;
      } else {
        databases?.[0]?.children?.forEach((table) => {
          selection[table.name] = true;
          selection.__selectionCount__ = databases?.[0]?.children?.length;
        });
      }
    });
  };

  const selectTables = useCallback(
    (tableNames: string[], databaseName: string, checked: boolean) => {
      updateSelection((newSelection) => {
        tableNames.forEach((name) => {
          newSelection[name] = checked;
        });
        newSelection.__selectionCount__ = Object.values(newSelection).filter(
          (value) => isBoolean(value) && !!value,
        ).length;
      });
    },
    [updateSelection],
  );

  const selectResyncTables = useCallback(
    (tableNames: string[], databaseName: string, checked: boolean) => {
      updateResyncSelection((newResyncSelection) => {
        tableNames.forEach((name) => {
          newResyncSelection[name] = checked;
        });
      });
    },
    [updateResyncSelection],
  );

  const addTableFilters = useCallback(
    (tableName: string, filter: string) => {
      updateTableFilters((newTableFilters) => {
        newTableFilters[tableName] = filter;
      });
    },
    [updateTableFilters],
  );

  const updateTableFilter = useCallback(
    (tableName: string, filter: string) => {
      updateTableFilters((newTableFilters) => {
        newTableFilters[tableName] = filter;
      });
    },
    [updateTableFilters],
  );

  const expandTable = (tableName, databaseName, expanded) => {
    setState((state) => {
      state.expandedTables[tableName] = expanded;
    });
  };

  const providerValue = useMemo(() => {
    return {
      ...state,
      selection,
      resyncSelection,
      tableFilters,
      columnSelections,
      syncModes,
      onShowSchema,
      onShowSamples,
      tableMap,
      credentialId: props.credentialId,
      onChangeSyncAll,
      isDestination: props.isDestination,
      parentScrollRef,
      expandTable,
      selectDatabase,
      selectTables,
      selectResyncTables,
      addTableFilters,
      updateTableFilters,
      updateTableFilter,
      updateColumnSelection,
      updateSyncMode,
    };
  }, [
    state,
    tableMap,
    props.credentialId,
    parentScrollRef,
    selection,
    resyncSelection,
    tableFilters,
    columnSelections,
    syncModes,
    props.isDestination,
    needsSelection,
    onShowSchema,
    onShowSamples,
    selectResyncTables,
    addTableFilters,
    updateTableFilters,
    updateTableFilter,
    updateColumnSelection,
    updateSyncMode,
  ]);

  const onChangeMapping = (map) => {
    mapRef.current = map;
  };

  const deleteMapping = () => {
    mapRef.current = {};
  };

  const updateTableMapping = (table) => {
    setTableMap((map) => {
      const newMap = {
        ...map,
        [table.name]: mapRef.current,
      };
      if (isEmpty(newMap[table.name])) {
        delete newMap[table.name];
      }
      props.onMappingChange(newMap);
      return newMap;
    });
  };

  const saveSchema = () => {
    updateTableMapping(state.table);
    setState((state) => {
      state.table = null;
    });
  };

  const selectedTable = useMemo(() => {
    if (!props.isDestination) return {};
    if (!state.table) return {};
    const selected = databases?.[0]?.children?.find((t) => t?.id === state.table?.id);
    return selected || {};
  }, [state.table, databases, props.isDestination]);

  const togglePreview = (show) => () => {
    setState((state) => {
      state.showPreview = show;
    });
  };

  const removeTable = () => {
    setState((state) => {
      state.table = null;
    });
  };

  return (
    <DatabasesContext.Provider value={providerValue}>
      {children}
      <ModalDefault
        open={state.showPreview}
        title={
          state.previewDatabase && state.previewTable
            ? `${state.previewDatabase}.${state.previewTable}`
            : 'Preview records'
        }
        onCancel={togglePreview(false)}
        onConfirm={togglePreview(false)}
      >
        <ModalPreviewSamples credentialId={credentialId} />
      </ModalDefault>
      <ModalDefault
        open={!!state.table}
        fullWidth
        title="Destination schema"
        onCancel={removeTable}
        onConfirm={saveSchema}
      >
        <ModalDestinationSchema
          table={selectedTable}
          mapping={tableMap[selectedTable.name]}
          sourceConnector={props.sourceConnector}
          sinkConnector={props.sinkConnector}
          onChangeMapping={onChangeMapping}
          deleteMapping={deleteMapping}
        />
      </ModalDefault>
    </DatabasesContext.Provider>
  );
};
